import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/styles';

import { Command, MenuListProps } from '../slash-command/types';

// DropdownButton component
const DropdownButton = React.forwardRef<
	HTMLButtonElement,
	{
		children: React.ReactNode;
		isActive?: boolean;
		onClick?: () => void;
		disabled?: boolean;
		className?: string;
	}
>(function DropdownButtonInner({ children, isActive, onClick, disabled, className }, ref) {
	const buttonClass = cn(
		'relative flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm font-medium outline-none transition-colors text-primary',
		!isActive && !disabled && 'hover:bg-accent',
		isActive && !disabled && 'bg-accent',
		disabled && 'pointer-events-none opacity-50',
		className
	);

	return (
		<button
			className={buttonClass}
			disabled={disabled}
			onClick={onClick}
			ref={ref}
		>
			{children}
		</button>
	);
});

// MenuList component
const MenuList = React.forwardRef((props: MenuListProps, ref) => {
	const scrollContainer = useRef<HTMLDivElement>(null);
	const activeItem = useRef<HTMLButtonElement>(null);
	const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
	const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

	// Anytime the groups change, i.e. the user types to narrow it down, we want to
	// reset the current selection to the first menu item
	useEffect(() => {
		setSelectedGroupIndex(0);
		setSelectedCommandIndex(0);
	}, [props.items]);

	const selectItem = useCallback(
		(groupIndex: number, commandIndex: number) => {
			const command = props.items[groupIndex].commands[commandIndex];
			props.command(command);
		},
		[props]
	);

	React.useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
			if (event.key === 'ArrowDown') {
				if (!props.items.length) {
					return false;
				}

				const commands = props.items[selectedGroupIndex].commands;

				let newCommandIndex = selectedCommandIndex + 1;
				let newGroupIndex = selectedGroupIndex;

				if (commands.length - 1 < newCommandIndex) {
					newCommandIndex = 0;
					newGroupIndex = selectedGroupIndex + 1;
				}

				if (props.items.length - 1 < newGroupIndex) {
					newGroupIndex = 0;
				}

				setSelectedCommandIndex(newCommandIndex);
				setSelectedGroupIndex(newGroupIndex);

				return true;
			}

			if (event.key === 'ArrowUp') {
				if (!props.items.length) {
					return false;
				}

				let newCommandIndex = selectedCommandIndex - 1;
				let newGroupIndex = selectedGroupIndex;

				if (newCommandIndex < 0) {
					newGroupIndex = selectedGroupIndex - 1;
					newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0;
				}

				if (newGroupIndex < 0) {
					newGroupIndex = props.items.length - 1;
					newCommandIndex = props.items[newGroupIndex].commands.length - 1;
				}

				setSelectedCommandIndex(newCommandIndex);
				setSelectedGroupIndex(newGroupIndex);

				return true;
			}

			if (event.key === 'Enter') {
				if (!props.items.length || selectedGroupIndex === -1 || selectedCommandIndex === -1) {
					return false;
				}

				selectItem(selectedGroupIndex, selectedCommandIndex);

				return true;
			}

			return false;
		},
	}));

	useEffect(() => {
		if (activeItem.current && scrollContainer.current) {
			const offsetTop = activeItem.current.offsetTop;
			const offsetHeight = activeItem.current.offsetHeight;

			scrollContainer.current.scrollTop = offsetTop - offsetHeight;
		}
	}, [selectedCommandIndex, selectedGroupIndex]);

	const createCommandClickHandler = useCallback(
		(groupIndex: number, commandIndex: number) => {
			return () => {
				selectItem(groupIndex, commandIndex);
			};
		},
		[selectItem]
	);

	if (!props.items.length) {
		return null;
	}

	return (
		<div
			ref={scrollContainer}
			className="z-50 max-h-[min(80vh,24rem)] min-w-48 overflow-y-auto rounded-lg border bg-popover p-1 text-primary shadow-md"
		>
			<div className="grid grid-cols-1">
				{props.items.map((group, groupIndex: number) => (
					<React.Fragment key={`${group.title}-wrapper`}>
						{/* Add divider before Lists group (second group) */}
						{groupIndex === 1 && <div className="my-1 h-px bg-border" />}
						{group.commands.map((command: Command, commandIndex: number) => (
							<DropdownButton
								key={`${command.label}`}
								ref={
									selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex
										? activeItem
										: null
								}
								isActive={
									selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex
								}
								onClick={createCommandClickHandler(groupIndex, commandIndex)}
							>
								{/* TODO - Put icon here */}
								{command.label}
							</DropdownButton>
						))}
					</React.Fragment>
				))}
			</div>
		</div>
	);
});

MenuList.displayName = 'MenuList';

export default MenuList;
